import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { cn } from "../../lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ui-ring focus-visible:ring-offset-2 focus-visible:ring-offset-ui-background disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-accent text-text-on-accent hover:bg-accent/90",
        secondary: "bg-ui-muted text-ui-foreground hover:bg-highlight",
        ghost:
          "text-ui-muted-foreground hover:bg-ui-muted hover:text-ui-foreground",
        outline:
          "border border-ui-border bg-ui-background text-ui-foreground hover:bg-ui-muted",
        destructive: "bg-delete text-text-on-accent hover:bg-delete-hover",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3",
        lg: "h-12 rounded-lg px-8 text-base font-semibold",
        icon: "h-8 w-8",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

/**
 * Reusable button primitive for chat and app actions.
 * @param {Object} props
 * @param {boolean} [props.asChild]
 * @param {string} [props.className]
 * @param {"default"|"secondary"|"ghost"|"outline"|"destructive"} [props.variant]
 * @param {"default"|"sm"|"lg"|"icon"} [props.size]
 * @returns {JSX.Element}
 */
const Button = React.forwardRef(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
